import axios from 'axios';
import { clearPersistedAuth, getAuthToken } from '../utils/storage';

let unauthorizedHandler = null;

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = typeof handler === 'function' ? handler : null;
};

export const handleUnauthorized = () => {
  clearPersistedAuth();
  unauthorizedHandler?.();

  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

export class ApiRequestError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.status = statusCode;
  }
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.risman.pro/api',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  },
  timeout: 60000
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      handleUnauthorized();
    }

    return Promise.reject(error);
  }
);

export const extractApiPayloadMessage = (payload) => {
  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return extractApiPayloadMessage(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const direct =
    payload.Message ||
    payload.message ||
    payload.Error ||
    payload.error;

  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }

  const nested = payload.data || payload.Data;

  if (nested && nested !== payload) {
    return extractApiPayloadMessage(nested);
  }

  return null;
};

export const getApiErrorMessage = (error, fallbackMessage = 'Request failed.') => {
  return (
    extractApiPayloadMessage(error?.response?.data) ||
    extractApiPayloadMessage(error?.message) ||
    fallbackMessage
  );
};

export const readFetchApiError = async (response) => {
  const text = await response.text();
  return extractApiPayloadMessage(text) || text || `HTTP ${response.status}`;
};

export default apiClient;
